import { useSelector } from '@/store'
import { useI18nText } from '@/utils/common'
import { useMemo } from 'react'
import { DEFAULT_LANG } from '@/constants'
import { useCurrentLanguage } from '@/hooks/usei18n'
import { useMemoizedFn } from './useMemoizedFn'
import { cloneDeep } from 'lodash'
import type { IWarehouseExpressItem, WarehouseInfoType } from '@/store/commonSlice/types'
import { REF_ARRAY } from '@/constants/reference'

/**
 * 仓库信息及其判断条件
 * 有传入warehouseId，则返回单仓信息及其判断
 * 未传入warehouseId，则是多仓的处理
 * @see /store/commonSlice/index.ts 方法 getWarehoseInfo 刷新
 */
export function useWarehouse(warehouseId?: string) {
  const language = useCurrentLanguage()
  const warehouseInfoList: WarehouseInfoType[] = useSelector((state) => state.common?.warehouseInfoList || REF_ARRAY)
  const autoWarehouseList = useSelector((state) => state.common?.autoWarehouseList || REF_ARRAY)
  const isMultipleWarehouse = useSelector((state) => state.common.isMultipleWarehouse)

  const enableWarehouseInfoList = useMemo(() => warehouseInfoList?.filter((item) => item.enable), [warehouseInfoList])
  const warehouseInfo = useMemo(() => warehouseId && warehouseInfoList?.find((warehouse) => warehouse.warehouseId === warehouseId), [warehouseId, warehouseInfoList])
  const isHK = useMemo(() => warehouseInfo?.country?.toLowerCase() === 'hk', [warehouseInfo])
  const isCN = useMemo(() => warehouseInfo?.country?.toLowerCase() === 'cn', [warehouseInfo])
  const warehouseCountry = warehouseInfo?.country

  const warehouseOptions = useMemo(
    () => [
      ...(enableWarehouseInfoList.map((item) => ({
        ...item,
        warehouseI18nName: item.warehouseNames?.[language.languageType] || item.warehouseNames?.[DEFAULT_LANG],
      })) ?? []),
    ],
    [enableWarehouseInfoList, language.languageType]
  )
  const { getI18nText } = useI18nText()
  const warehouseMap = useMemo(() => {
    return warehouseInfoList?.reduce((prev, cur) => {
      return {
        ...prev,
        [cur.warehouseId]: getI18nText(cur.warehouseNames),
      }
    }, {} as Record<string, string>)
  }, [getI18nText, warehouseInfoList])

  const getWarehoseInfo = useMemoizedFn((warehouseId: string) => warehouseInfoList?.find((warehouse) => warehouse.warehouseId === warehouseId))

  const getExpressList = useMemoizedFn((warehouseId: string) => {
    if (!warehouseId) return []
    return warehouseInfoList
      ?.find((item) => item?.warehouseId === warehouseId)
      ?.warehouseExpresses.filter((item) => item.enable)
      ?.map((item) => {
        const newItem = cloneDeep(item)
        newItem.i18nLogisticsName = getI18nText(newItem.logisticsNames)
        newItem.i18nLogisticsProviderNames = getI18nText(newItem.logisticsProviderNames)
        return newItem
      })
  })

  const utf8ValidateProviderIds = useSelector((state) => state.configSlice.utf8validate)
  const utf8ValidateWarehouseId = warehouseInfoList
    ?.filter((warehouse) => warehouse?.enable && utf8ValidateProviderIds.includes(warehouse?.providerId))
    ?.map((warehouse) => warehouse?.warehouseId)
  /**
   * @deprecated
   * 原来用来获取utf-8限制的服务商，现在有utf8ValidateWarehouseId，可以废弃SFWarehouseIds
   */
  const SFWarehouseIds = warehouseInfoList?.filter((warehouse) => warehouse?.enable && warehouse?.providerId === 'SF')?.map((warehouse) => warehouse?.warehouseId)

  /** 所有仓库下的所有物流渠道 */
  const allExpressList = useMemo<IWarehouseExpressItem[]>(
    () =>
      warehouseInfoList.reduce((prev, next) => {
        const { warehouseId } = next
        const expressList = getExpressList(warehouseId)
        return [...prev, ...expressList]
      }, []),
    [getExpressList, warehouseInfoList]
  )

  // 根据仓库ID返回数据
  if (warehouseId) {
    return {
      /** 选择仓库是HK */
      isHK,
      /** 选择仓库是CN */
      isCN,
      /** 传入的仓库ID对应的仓库 */
      warehouseInfo,
      /** 仓库列表 */
      warehouseInfoList,
      /** 开启的仓库列表 */
      enableWarehouseInfoList,
      /**仓库id => name Map */
      warehouseMap,
      /**仓库多语言 */
      warehouseOptions,
      /** 顺丰仓id */
      SFWarehouseIds,
      /** 所有仓库下的所有物流渠道 */
      allExpressList,
      /** 仓库国家 */
      warehouseCountry,
      /** 需要utf-8校验的服务商 */
      utf8ValidateWarehouseId,
    }
  }

  return {
    /** 仓库列表 */
    warehouseInfoList,
    /** 启用的自动选仓列表 */
    autoWarehouseList,
    /** 开启的仓库列表 */
    enableWarehouseInfoList,
    /**仓库id => name Map */
    warehouseMap,
    /**仓库多语言 */
    warehouseOptions,
    /**获取单仓信息 */
    getWarehoseInfo,
    /**获取仓库对于启用物流 */
    getExpressList,
    /** 顺丰仓id */
    SFWarehouseIds,
    /** 所有仓库下的所有物流渠道 */
    allExpressList,
    /** 是否多仓 */
    isMultipleWarehouse,
    /** 需要utf-8校验的服务商 */
    utf8ValidateWarehouseId,
  }
}
